import React, { useState, useEffect, useCallback } from "react";
import {
  Row,
  Col,
  Input,
  Table,
  Button,
  Tag,
  Avatar,
  Spin,
  Alert,
  Card,
  message,
  Empty,
  Tooltip,
  Modal,
} from "antd";
import { Link, useNavigate } from "react-router-dom";
import {
  ReloadOutlined,
  CheckOutlined,
  CloseOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import moment from "moment";
import "moment/locale/es";
import MarketplaceStatus from "../MarketplaceStatus";
import './OrdersTable.scss';

const { Search } = Input;

moment.locale("es");

export default function OrdersTable({ orders = [], loading = false, role, onStatusChange }) {
  const navigate = useNavigate();

  const [tableData, setTableData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [searchedValue, setSearchedValue] = useState("");
  const [tableFilters, setTableFilters] = useState({});
  const [tableSorter, setTableSorter] = useState({
    field: "created_at",
    order: "descend",
  });
  const [paginationConfig, setPaginationConfig] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [error, setError] = useState(null);

  const [statusModalVisible, setStatusModalVisible] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [statusChangeLoading, setStatusChangeLoading] = useState(false);

  const statusOptions = [
    { value: 1, label: "Aceptada", color: "purple" },
    { value: 2, label: "En camino", color: "orange" },
    { value: 3, label: "Entregada", color: "green" },
    { value: 4, label: "Cancelada", color: "red" },
    { value: 5, label: "Rechazada", color: "volcano" },
    { value: 9, label: "Pendiente", color: "cyan" },
  ];

  const formatDaysAgo = (dateString) => {
    if (!dateString) return "Fecha no disponible";

    const date = moment(dateString);
    if (!date.isValid()) return "Fecha inválida";

    const now = moment();
    const diffMinutes = now.diff(date, "minutes");
    const diffHours = now.diff(date, "hours");
    const diffDays = now.diff(date, "days");
    const diffWeeks = now.diff(date, "weeks");
    const diffMonths = now.diff(date, "months");
    const diffYears = now.diff(date, "years");

    if (diffMinutes < 1) {
      return "Hace un momento";
    } else if (diffMinutes < 60) {
      return `Hace ${diffMinutes} ${diffMinutes === 1 ? "minuto" : "minutos"}`;
    } else if (diffHours < 24) {
      return `Hace ${diffHours} ${diffHours === 1 ? "hora" : "horas"}`;
    } else if (diffDays < 7) {
      return `Hace ${diffDays} ${diffDays === 1 ? "día" : "días"}`;
    } else if (diffWeeks < 4) {
      return `Hace ${diffWeeks} ${diffWeeks === 1 ? "semana" : "semanas"}`;
    } else if (diffMonths < 12) {
      return `Hace ${diffMonths} ${diffMonths === 1 ? "mes" : "meses"}`;
    } else {
      return `Hace ${diffYears} ${diffYears === 1 ? "año" : "años"}`;
    }
  };

  const handleStatusClick = (order) => {
    if (role === "6") {
      if (order.order_status === 9) {
        setSelectedOrder(order);
        setStatusModalVisible(true);
      } else if (order.order_status === 1) {
        handleMarkAsInTransit(order);
      } else if (order.order_status === 2) {
        handleMarkAsDelivered(order);
      }
    }
  };

  const handleMarkAsInTransit = (order) => {
    Modal.confirm({
      title: '¿Marcar orden como "En camino"?',
      icon: <ExclamationCircleOutlined />,
      content: `La orden ${order.order_id} será marcada como "En camino".`,
      okText: "Sí, confirmar",
      cancelText: "Cancelar",
      onOk: () => updateOrderStatus(order, 2),
    });
  };

  const handleMarkAsDelivered = (order) => {
    Modal.confirm({
      title: '¿Marcar orden como "Entregada"?',
      icon: <ExclamationCircleOutlined />,
      content: `La orden ${order.order_id} será marcada como "Entregada". ¿Estás seguro de que la orden fue entregada correctamente?`,
      okText: "Sí, confirmar entrega",
      cancelText: "Cancelar",
      onOk: () => updateOrderStatus(order, 3),
    });
  };

  const updateOrderStatus = async (order, newStatus) => {
    setStatusChangeLoading(true);
    
    const updatedData = tableData.map(item =>
      item.order_id === order.order_id
        ? { ...item, order_status: newStatus }
        : item
    );
    setTableData(updatedData);
    setFilteredData(prev => prev.map(item =>
      item.order_id === order.order_id
        ? { ...item, order_status: newStatus }
        : item
    ));
    
    try {
      const success = await onStatusChange(order.order_id, newStatus);
      if (!success) {
        setTableData(tableData);
        setFilteredData(filteredData);
        setError("Error al actualizar el estado");
      } else {
        setError(null);
      }
    } catch (error) {
      setTableData(tableData);
      setFilteredData(filteredData);
      setError("Error al actualizar el estado");
      message.error("Error al actualizar el estado");
    } finally {
      setStatusChangeLoading(false);
    }
  };

  const handleStatusModalClose = () => {
    setStatusModalVisible(false);
    setSelectedOrder(null);
    setStatusChangeLoading(false);
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedOrder) return;
    await updateOrderStatus(selectedOrder, newStatus);
    handleStatusModalClose();
  };

  const showStatusConfirm = (action, status) => {
    Modal.confirm({
      title: `¿Estás seguro de que deseas ${action} esta orden?`,
      icon: <ExclamationCircleOutlined />,
      content: `La orden ${selectedOrder?.order_id} será ${
        action === "aceptar" ? "aceptada" : "rechazada"
      }.`,
      okText: "Sí, confirmar",
      cancelText: "Cancelar",
      okType: action === "aceptar" ? "primary" : "danger",
      onOk: () => handleStatusChange(status),
      onCancel: handleStatusModalClose,
    });
  };

  const getStatusText = (status) => {
    const statusMap = {
      1: "Aceptada",
      2: "En camino",
      3: "Entregada",
      4: "Cancelada",
      5: "Rechazada",
      9: "Pendiente",
    };
    return statusMap[status] || "Desconocido";
  };

  const transformOrdersToTableData = useCallback((ordersList) => {
    if (!Array.isArray(ordersList)) return [];

    return ordersList.map((order, index) => {
      const orderId = order?.id || order?.order_id || `MKT-${index + 1}`;
      const products = Array.isArray(order?.products) ? order.products : [];
      const clientName = order?.client_name || order?.client?.name || "Cliente no disponible";
      const clientBusiness = order?.client_bussiness || order?.client?.business || "Empresa no disponible";
      const status = typeof order?.status === "number" ? order.status : (order?.order_status || 9);
      const price = order?.price || order?.total_price || 0;
      const createdAt = order?.created_at || order?.createdAt || new Date().toISOString();

      let daysAgo = formatDaysAgo(createdAt);

      const productNames = products.map((p) => p.name).join(" ");

      return {
        key: orderId,
        order_id: orderId,
        order_products: products,
        product_names: productNames,
        client_name: clientName,
        client_business: clientBusiness,
        order_status: status,
        order_status_text: getStatusText(status),
        total_price: price,
        total_price_text: formatPrice(price),
        created_at: createdAt,
        created_at_formatted: moment(createdAt).isValid()
          ? moment(createdAt).format("DD/MM/YYYY")
          : "Inválida",
        days_of_age: daysAgo,
        delivery_date: order?.deliveryDate || null,
        updated_at: order?.updated_at || null,
        total_price_num:
          typeof price === "number" ? price : parseFloat(price) || 0,
        created_at_num: new Date(createdAt).getTime(),
      };
    });
  }, []);

  const sortDataByNewest = (data) => {
    return [...data].sort((a, b) => {
      return (b.created_at_num || 0) - (a.created_at_num || 0);
    });
  };

  const applyFilters = useCallback(() => {
    let filtered = tableData;

    if (searchedValue.trim()) {
      const searchValue = searchedValue.trim().toLowerCase();
      filtered = filtered.filter((item) => {
        return (
          (item.order_id &&
            item.order_id.toLowerCase().includes(searchValue)) ||
          (item.client_name &&
            item.client_name.toLowerCase().includes(searchValue)) ||
          (item.client_business &&
            item.client_business.toLowerCase().includes(searchValue)) ||
          (item.product_names &&
            item.product_names.toLowerCase().includes(searchValue)) ||
          (item.order_status_text &&
            item.order_status_text.toLowerCase().includes(searchValue)) ||
          (item.total_price_text &&
            item.total_price_text.toLowerCase().includes(searchValue)) ||
          (item.created_at_formatted &&
            item.created_at_formatted.toLowerCase().includes(searchValue)) ||
          (item.days_of_age &&
            item.days_of_age.toLowerCase().includes(searchValue))
        );
      });
    }
    
    if (tableFilters.order_status && tableFilters.order_status.length > 0) {
      filtered = filtered.filter(item => 
        tableFilters.order_status.includes(item.order_status)
      );
    }
    
    filtered = sortDataByNewest(filtered);

    setFilteredData(filtered);
    setPaginationConfig((prev) => {
      const total = filtered.length;
      const maxPage = Math.max(1, Math.ceil(total / prev.pageSize));
      return {
        ...prev,
        total,
        current: Math.min(prev.current, maxPage),
      };
    });
  }, [tableData, searchedValue, tableFilters]);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchedValue(value);
    if (value === "") {
      applyFilters();
    }
  };

  const handleTableChange = (pagination, filters, sorter) => {
    const nextOrderStatusFilters = filters?.order_status || null;
    const currentOrderStatusFilters = tableFilters.order_status || null;
    const hasStatusFilterChanged =
      JSON.stringify(nextOrderStatusFilters) !==
      JSON.stringify(currentOrderStatusFilters);

    if (hasStatusFilterChanged) {
      setTableFilters(filters || {});
      setPaginationConfig((prev) => ({
        ...prev,
        current: 1,
      }));
    }
    
    if (sorter && sorter.field) {
      setTableSorter({
        field: sorter.field,
        order: sorter.order,
      });
    }

    if (pagination) {
      setPaginationConfig((prev) => ({
        ...prev,
        current: pagination.current,
        pageSize: pagination.pageSize,
      }));
    }
  };

  const handleResetFilters = () => {
    setSearchedValue("");
    setTableFilters({});
    setTableSorter({ field: "created_at", order: "descend" });
    setPaginationConfig({
      current: 1,
      pageSize: 10,
      total: filteredData.length,
    });
    setError(null);
  };

  const handleSearchEnter = (value) => {
    setSearchedValue(value);
    applyFilters();
  };

  const handleRefresh = () => {
    window.location.reload();
  };

  const formatPrice = (price) => {
    if (typeof price === "number") {
      return `$${price.toFixed(2)}`;
    }
    if (typeof price === "string") {
      const numPrice = parseFloat(price);
      return isNaN(numPrice) ? "Consultar precio" : `$${numPrice.toFixed(2)}`;
    }
    return "Consultar precio";
  };

  useEffect(() => {
    if (orders && orders.length > 0) {
      const transformedData = transformOrdersToTableData(orders);
      const sortedData = sortDataByNewest(transformedData);
      setTableData(sortedData);
      setFilteredData(sortedData);
      setPaginationConfig(prev => ({
        ...prev,
        total: sortedData.length,
      }));
      setError(null);
    } else {
      setTableData([]);
      setFilteredData([]);
      setPaginationConfig(prev => ({
        ...prev,
        total: 0,
      }));
    }
  }, [orders, transformOrdersToTableData]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const statusFilters = statusOptions.map((option) => ({
    text: (
      <Tag color={option.color} style={{ margin: "2px" }}>
        {option.label}
      </Tag>
    ),
    value: option.value,
  }));

  const columns = [
    {
      title: "# Orden",
      dataIndex: "order_id",
      key: "order_id",
      align: "center",
      width: "12%",
      fixed: 'left',
      sorter: (a, b) => (a.order_id || "").localeCompare(b.order_id || ""),
      sortDirections: ["ascend", "descend"],
      render: (text, record) => (
        <Link to={`/marketplace/orders/details/${record.order_id}`}>
          {text}
        </Link>
      ),
    },
    {
      title: "Productos",
      dataIndex: "order_products",
      key: "order_products",
      width: "44%",
      render: (products) => (
        <div>
          {products && products.length > 0 ? (
            <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
              <Avatar.Group shape="square" max={{ count: 2 }} size="small">
                {products.map((product, index) => (
                  <Avatar
                    key={product.id || index}
                    src={product.imgs && product.imgs[0]}
                    alt={product.name}
                    style={{ margin: "0 2px" }}
                  >
                    {!product.imgs || product.imgs.length === 0
                      ? (product.name || "P").charAt(0)
                      : null}
                  </Avatar>
                ))}
              </Avatar.Group>
              <div style={{ flex: 1, minWidth: 0 }}>
                {products.map((product, index) => (
                  <div
                    key={product.id || index}
                    style={{
                      fontSize: "12px",
                      lineHeight: "1.3",
                      marginBottom: "2px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {product.name}
                    {product.quantity > 1 && (
                      <span style={{ color: "#666", marginLeft: "4px" }}>
                        (x{product.quantity})
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <Tag color="default">Sin productos</Tag>
          )}
        </div>
      ),
    },
    {
      title: "Total",
      dataIndex: "total_price",
      key: "total_price",
      align: "center",
      width: "10%",
      sorter: (a, b) => (a.total_price_num || 0) - (b.total_price_num || 0),
      sortDirections: ["ascend", "descend"],
      render: (price) => (
        <Tag color="blue" style={{ fontWeight: 600 }}>
          {formatPrice(price)}
        </Tag>
      ),
    },
    {
      title: "Estatus",
      dataIndex: "order_status",
      key: "order_status",
      align: "center",
      width: "12%",
      filters: statusFilters,
      filteredValue: tableFilters.order_status || null,
      onFilter: (value, record) => record.order_status === value,
      render: (status, record) => {
        const isSupplier = role === "6";
        const statusContent = <MarketplaceStatus status={status} />;

        if (isSupplier && (status === 9 || status === 1 || status === 2)) {
          let tooltipText = "";
          if (status === 9) tooltipText = "Haz clic para aceptar o rechazar la orden";
          else if (status === 1) tooltipText = "Haz clic para marcar como 'En camino'";
          else if (status === 2) tooltipText = "Haz clic para marcar como 'Entregada'";

          return (
            <Tooltip title={tooltipText} placement="top">
              <div
                className="status-action-trigger"
                onClick={(event) => {
                  event.stopPropagation();
                  handleStatusClick(record);
                }}
                style={{
                  cursor: "pointer",
                  display: "inline-block",
                }}
              >
                {statusContent}
              </div>
            </Tooltip>
          );
        }
        
        return statusContent;
      },
      sorter: (a, b) => (a.order_status || 0) - (b.order_status || 0),
      sortDirections: ["ascend", "descend"],
    },
    {
      title: "Fecha Creación",
      dataIndex: "created_at",
      key: "created_at",
      align: "center",
      width: "11%",
      sorter: (a, b) => (a.created_at_num || 0) - (b.created_at_num || 0),
      sortDirections: ["ascend", "descend"],
      defaultSortOrder: "descend",
      render: (date) =>
        moment(date).isValid() ? moment(date).format("DD/MM/YYYY") : "Inválida",
    },
    {
      title: "Antigüedad",
      dataIndex: "days_of_age",
      key: "days_of_age",
      align: "center",
      width: "11%",
      sorter: (a, b) => (a.created_at_num || 0) - (b.created_at_num || 0),
      sortDirections: ["ascend", "descend"],
      render: (text) => <div style={{ fontSize: "12px" }}>{text}</div>,
    },
  ];

  const getOrderStats = () => {
    if (!Array.isArray(tableData)) {
      return {
        total: 0,
        pending: 0,
        accepted: 0,
        inTransit: 0,
        delivered: 0,
        canceled: 0,
      };
    }

    const total = tableData.length;
    const pending = tableData.filter((order) => order.order_status === 9).length;
    const accepted = tableData.filter((order) => order.order_status === 1).length;
    const inTransit = tableData.filter((order) => order.order_status === 2).length;
    const delivered = tableData.filter((order) => order.order_status === 3).length;
    const canceled = tableData.filter((order) => order.order_status === 4).length;
    const rejected = tableData.filter((order) => order.order_status === 5).length;

    return { total, pending, accepted, inTransit, delivered, canceled, rejected };
  };

  const stats = getOrderStats();

  const hasActiveFilters =
    searchedValue.trim() !== "" ||
    (tableFilters.order_status && tableFilters.order_status.length > 0);

  const getSelectedStatusLabels = () => {
    if (!tableFilters.order_status) return [];
    return tableFilters.order_status
      .map((status) => statusOptions.find((opt) => opt.value === status)?.label)
      .filter(Boolean);
  };

  const isStatusCardActive = (statusValues) => {
    const current = tableFilters.order_status || [];
    if (current.length !== statusValues.length) return false;
    return statusValues.every((value) => current.includes(value));
  };

  const handleStatusCardClick = (statusValues = []) => {
    const shouldClearFilter = isStatusCardActive(statusValues) || statusValues.length === 0;
    setTableFilters(shouldClearFilter ? {} : { order_status: statusValues });
    setPaginationConfig((prev) => ({
      ...prev,
      current: 1,
    }));
  };

  if (loading && orders.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" />
        <p style={{ marginTop: 16 }}>Cargando órdenes...</p>
      </div>
    );
  }

  return (
    <div className="orders-table-container">
      <Modal
        title={`Cambiar estado de orden ${selectedOrder?.order_id}`}
        open={statusModalVisible}
        onCancel={handleStatusModalClose}
        footer={[
          <Button
            key="cancel"
            onClick={handleStatusModalClose}
            disabled={statusChangeLoading}
          >
            Cancelar
          </Button>,
          <Button
            key="reject"
            danger
            icon={<CloseOutlined />}
            onClick={() => showStatusConfirm("rechazar", 5)}
            loading={statusChangeLoading}
          >
            Rechazar Orden
          </Button>,
          <Button
            key="accept"
            type="primary"
            icon={<CheckOutlined />}
            onClick={() => showStatusConfirm("aceptar", 1)}
            loading={statusChangeLoading}
          >
            Aceptar Orden
          </Button>,
        ]}
      >
        <div style={{ padding: "20px 0", textAlign: "center" }}>
          <ExclamationCircleOutlined
            style={{ fontSize: "48px", color: "#faad14", marginBottom: "16px" }}
          />
          <h3>¿Qué acción deseas realizar con esta orden?</h3>
          <p style={{ color: "#666", marginBottom: "0" }}>
            La orden <strong>{selectedOrder?.order_id}</strong> está actualmente
            en estado <strong>Pendiente</strong>.
          </p>
        </div>
      </Modal>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={20} md={21} lg={22}>
          <Input
            placeholder="Buscar en todas las columnas..."
            allowClear
            size="large"
            value={searchedValue}
            onChange={handleInputChange}
            onPressEnter={(e) => handleSearchEnter(e.target.value)}
            style={{ width: "100%" }}
          />
        </Col>
        <Col xs={24} sm={4} md={3} lg={2}>
          <Tooltip title="Actualizar página" placement="top">
            <Button
              type="primary"
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={loading}
              style={{ width: "100%", height: "40px" }}
            />
          </Tooltip>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
          <Card
            size="small"
            hoverable
            onClick={() => handleStatusCardClick([])}
            style={{
              textAlign: "center",
              borderRadius: "8px",
              cursor: "pointer",
              border: !tableFilters.order_status ? "2px solid #1890ff" : "1px solid #f0f0f0",
            }}
          >
            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#1890ff" }}>
              {stats.total}
            </div>
            <div style={{ fontSize: "12px", color: "#666" }}>Total Órdenes</div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
          <Card
            size="small"
            hoverable
            onClick={() => handleStatusCardClick([9])}
            style={{
              textAlign: "center",
              borderRadius: "8px",
              cursor: "pointer",
              border: isStatusCardActive([9]) ? "2px solid #faad14" : "1px solid #f0f0f0",
            }}
          >
            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#faad14" }}>
              {stats.pending}
            </div>
            <div style={{ fontSize: "12px", color: "#666" }}>Pendientes</div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
          <Card
            size="small"
            hoverable
            onClick={() => handleStatusCardClick([1])}
            style={{
              textAlign: "center",
              borderRadius: "8px",
              cursor: "pointer",
              border: isStatusCardActive([1]) ? "2px solid #722ed1" : "1px solid #f0f0f0",
            }}
          >
            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#722ed1" }}>
              {stats.accepted}
            </div>
            <div style={{ fontSize: "12px", color: "#666" }}>Aceptada</div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
          <Card
            size="small"
            hoverable
            onClick={() => handleStatusCardClick([2])}
            style={{
              textAlign: "center",
              borderRadius: "8px",
              cursor: "pointer",
              border: isStatusCardActive([2]) ? "2px solid #fa8c16" : "1px solid #f0f0f0",
            }}
          >
            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#fa8c16" }}>
              {stats.inTransit}
            </div>
            <div style={{ fontSize: "12px", color: "#666" }}>En Camino</div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
          <Card
            size="small"
            hoverable
            onClick={() => handleStatusCardClick([3])}
            style={{
              textAlign: "center",
              borderRadius: "8px",
              cursor: "pointer",
              border: isStatusCardActive([3]) ? "2px solid #52c41a" : "1px solid #f0f0f0",
            }}
          >
            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#52c41a" }}>
              {stats.delivered}
            </div>
            <div style={{ fontSize: "12px", color: "#666" }}>Entregadas</div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
          <Card
            size="small"
            hoverable
            onClick={() => handleStatusCardClick([4])}
            style={{
              textAlign: "center",
              borderRadius: "8px",
              cursor: "pointer",
              border: isStatusCardActive([4]) ? "2px solid #ff4d4f" : "1px solid #f0f0f0",
            }}
          >
            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#ff4d4f" }}>
              {stats.canceled}
            </div>
            <div style={{ fontSize: "12px", color: "#666" }}>Canceladas</div>
          </Card>
        </Col>
        <Col xs={24} sm={12} md={8} lg={6} xl={4}>
          <Card
            size="small"
            hoverable
            onClick={() => handleStatusCardClick([5])}
            style={{
              textAlign: "center",
              borderRadius: "8px",
              cursor: "pointer",
              border: isStatusCardActive([5]) ? "2px solid #d4380d" : "1px solid #f0f0f0",
            }}
          >
            <div style={{ fontSize: "24px", fontWeight: "bold", color: "#d4380d" }}>
              {stats.rejected}
            </div>
            <div style={{ fontSize: "12px", color: "#666" }}>Rechazadas</div>
          </Card>
        </Col>
      </Row>

      {hasActiveFilters && (
        <Alert
          message={
            <div>
              <strong>Filtros activos:</strong>
              {searchedValue && (
                <Tag style={{ marginLeft: 8 }} color="blue">
                  Búsqueda: "{searchedValue}"
                </Tag>
              )}
              {tableFilters.order_status &&
                tableFilters.order_status.length > 0 && (
                  <Tag style={{ marginLeft: 8 }} color="green">
                    Estatus: {getSelectedStatusLabels().join(", ")}
                  </Tag>
                )}
              <span style={{ marginLeft: 8 }}>
                - {filteredData.length} resultado(s) de {tableData.length}
              </span>
            </div>
          }
          type="info"
          showIcon
          style={{ marginBottom: 16, borderRadius: "6px" }}
          action={
            <Button size="small" onClick={handleResetFilters}>
              Limpiar Filtros
            </Button>
          }
        />
      )}

      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 16, borderRadius: "6px" }}
          action={
            <Button size="small" onClick={handleRefresh}>
              Reintentar
            </Button>
          }
        />
      )}

      <div className="orders-table-wrapper">
        <Table
          className="orders-table"
          columns={columns}
          dataSource={filteredData}
          loading={loading}
          onChange={handleTableChange}
          onRow={(record) => ({
            onClick: (event) => {
              if (event.target.closest("a")) {
                return;
              }

              if (event.target.closest(".status-action-trigger")) {
                return;
              }

              navigate(`/marketplace/orders/details/${record.order_id}`);
            },
            style: { cursor: "pointer" },
          })}
          tableLayout="fixed"
          scroll={{ x: "100%" }}
          style={{ width: "100%" }}
          pagination={{
            current: paginationConfig.current,
            pageSize: paginationConfig.pageSize,
            total: paginationConfig.total,
            showSizeChanger: true,
            showQuickJumper: false,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} de ${total} órdenes`,
            pageSizeOptions: ["10", "20", "50", "100"],
          }}
          locale={{
            emptyText: loading ? (
              <Spin size="large" />
            ) : hasActiveFilters ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  <div>
                    <p>No se encontraron órdenes con los filtros aplicados</p>
                    <Button type="link" onClick={handleResetFilters}>
                      Mostrar todas las {tableData.length} órdenes
                    </Button>
                  </div>
                }
              />
            ) : (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No hay órdenes disponibles"
              />
            ),
          }}
        />
      </div>
    </div>
  );
}